import React, { useEffect, useState } from 'react'; // useState kept for loading/error/app state
import { useParams, NavLink } from 'react-router-dom';
import {
  BodyText,
  FlexColumn,
  FlexRow,
  formatDate,
  formatNumber,
  SvgIcon
} from '@make-software/cspr-design';
import styled from 'styled-components';
import { getPublicAppView, PublicAppViewResponse } from 'app/console-api/apps/apps';
import { AppContactsView } from 'app/components/common/contacts/app-contacts-view';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { CategoryTag } from 'app/components/common/categories/category-tag';
import { AppType } from 'app/components/common/apps/app-type';
import { BoldText, StyledStatusElement } from 'app/components/common/table/styled-elements';
import DetailsTableRow from 'app/components/management/common/details-table-row/details-table-row';
import DetailsNameTableData from 'app/components/management/common/details-name-table-data/details-name-table-data';
import DetailsValueTableData from 'app/components/management/common/details-value-table-data/details-value-table-data';
import { PageContainer, ViewPageContainer } from 'app/components/common/common-styles';
import { MARKETPLACE_PATH } from 'app/route/paths';

const Container = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    margin: ['0', '10', '0px 48px 48px 48px'],
    padding: '24px'
  })
);

const BreadcrumbLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 16,
  color: theme.styleguideColors.fillPrimaryBlue,
  cursor: 'pointer'
}));

const BreadcrumbSeparator = styled.span(({ theme }) => ({
  color: theme.styleguideColors.fillPrimaryBlue,
  fontSize: 16
}));

const BreadcrumbCurrent = styled.span(({ theme }) => ({
  color: theme.styleguideColors.contentSecondary,
  fontSize: 16
}));

const StyledCaption = styled.div<{ margin?: string }>(({ theme, margin }) =>
  theme.withMedia({
    margin: margin || '24px 0 32px 17px'
  })
);

const NoContactsMessage = styled.div(({ theme }) =>
  theme.withMedia({
    padding: '16px',
    backgroundColor: theme.styleguideColors.backgroundPrimary,
    borderRadius: '8px',
    textAlign: 'center',
    color: theme.styleguideColors.contentSecondary
  })
);

export const PublicAppPage = () => {
  const { appId } = useParams<{ appId: string }>();
  const [app, setApp] = useState<PublicAppViewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!appId) {
      setError('App ID is required');
      setLoading(false);
      return;
    }

    getPublicAppView(appId)
      .then((data) => {
        setApp(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load app');
        setLoading(false);
      });
  }, [appId]);

  if (loading) {
    return (
      <Container>
        <h1>App Profile</h1>
        <PageContainer>
          <ViewPageContainer>
            <SkeletonLoader height={'300px'} />
            <SkeletonLoader height={'200px'} />
          </ViewPageContainer>
        </PageContainer>
      </Container>
    );
  }

  if (error || !app) {
    return (
      <ErrorWindow
        withModal
        title="Error"
        information={error || 'App not found'}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <Container>
      <FlexRow gap={8} wrap={'wrap'} style={{ marginBottom: '24px' }}>
        <BreadcrumbLink to={MARKETPLACE_PATH}>Marketplace</BreadcrumbLink>
        <BreadcrumbSeparator>›</BreadcrumbSeparator>
        <BreadcrumbCurrent>{app.name}</BreadcrumbCurrent>
      </FlexRow>
      <FlexColumn style={{ width: '100%' }} gap={32}>
        <PageContainer>
          <ViewPageContainer>
            <FlexColumn>
              <StyledCaption>
                <BodyText size={1} scale={'lg'}>
                  App Details
                </BodyText>
              </StyledCaption>
              <DetailsTableRow>
                <DetailsNameTableData>Type</DetailsNameTableData>
                <DetailsValueTableData>
                  <AppType type={app.type} />
                </DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>Name</DetailsNameTableData>
                <DetailsValueTableData>{app.name}</DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>Description</DetailsNameTableData>
                <DetailsValueTableData>{app.description}</DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>Status</DetailsNameTableData>
                <DetailsValueTableData>
                  <BoldText size={4} monotype>
                    {app.isActive ? (
                      <StyledStatusElement name={'Active'} isActive={true} />
                    ) : (
                      <StyledStatusElement name={'Inactive'} isActive={false} />
                    )}
                  </BoldText>
                </DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>URL</DetailsNameTableData>
                <DetailsValueTableData>
                  <a href={app.url} target="_blank" rel="noopener noreferrer">
                    {app.url}
                  </a>
                </DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>Categories</DetailsNameTableData>
                <DetailsValueTableData>
                  <FlexRow gap={4}>
                    {app.categories.map((categoryName) => (
                      <CategoryTag key={categoryName} name={categoryName} />
                    ))}
                  </FlexRow>
                </DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>MAU</DetailsNameTableData>
                <DetailsValueTableData>{formatNumber(app.mau)}</DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>DAU</DetailsNameTableData>
                <DetailsValueTableData>{formatNumber(app.dau)}</DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>Registered At</DetailsNameTableData>
                <DetailsValueTableData>{formatDate(app.createdAt.toString())}</DetailsValueTableData>
              </DetailsTableRow>
            </FlexColumn>
          </ViewPageContainer>
        </PageContainer>

        <PageContainer>
          <ViewPageContainer>
            <FlexColumn>
              <StyledCaption>
                <BodyText size={1} scale={'lg'}>
                  Contacts
                </BodyText>
              </StyledCaption>
              {app.contacts && app.contacts.length > 0 ? (
                <AppContactsView contacts={app.contacts} />
              ) : (
                <NoContactsMessage>
                  <BodyText size={3} variation={'darkGray'}>
                    Contacts are available only for partners. Request a deal to access contact
                    information.
                  </BodyText>
                </NoContactsMessage>
              )}
            </FlexColumn>
          </ViewPageContainer>
        </PageContainer>
      </FlexColumn>
    </Container>
  );
};
